import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Copy, Eye, MoreHorizontal, Plus, ShieldAlert, Trash2, UsersRound } from 'lucide-react';
import { toast } from 'sonner';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { useSegmentListQuery } from '@/client/api/campaigns/lifecycle.query';
import { useCreateSegmentMutation, useDeleteSegmentMutation } from '@/client/api/campaigns/lifecycle.mutation';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import type { Segment } from '@/client/types/campaigns';
import { formatEpoch, segmentId } from '@/client/types/campaigns';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import { useHasPermission } from '@/client/hooks/useHasPermission';
import { generateIdempotencyKey } from '@/client/utils/requestSecurity';

const AccessDenied = () => (
  <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-gray-100 bg-white py-20 text-center shadow-sm">
    <div className="flex h-14 w-14 items-center justify-center rounded-full bg-red-50">
      <ShieldAlert className="h-7 w-7 text-red-500" />
    </div>
    <h2 className="text-lg font-semibold text-gray-800">Access restricted</h2>
    <p className="max-w-sm text-sm text-gray-500">
      Requires <code className="rounded bg-gray-100 px-1.5 py-0.5 text-xs">campaign.view</code>.
    </p>
  </div>
);

const SegmentListPage = () => {
  useBreadcrumbs([
    { label: 'Campaigns', href: '/admin/relationship/campaigns' },
    { label: 'Segments' },
  ]);

  const viewPerm = useHasPermission('campaign.view');
  const segmentPerm = useHasPermission('campaign.segment');
  const canView = viewPerm.isSuperAdmin || viewPerm.hasPermission;
  const canWrite = segmentPerm.isSuperAdmin || segmentPerm.hasPermission;
  const permLoading = viewPerm.isLoading;

  const { data, isLoading, isError } = useSegmentListQuery(canView && !permLoading);
  const del = useDeleteSegmentMutation();
  const create = useCreateSegmentMutation();
  const navigate = useNavigate();
  const segments = data?.segments ?? [];
  const [toDelete, setToDelete] = useState<Segment | null>(null);

  const handleDelete = async () => {
    if (!toDelete) return;
    try {
      await del.mutateAsync({ sid: segmentId(toDelete), idempotencyKey: generateIdempotencyKey() });
      toast.success('Segment deleted', { richColors: true });
      setToDelete(null);
    } catch (error: any) {
      toast.error(error?.message || 'Delete failed', { richColors: true });
    }
  };

  const handleDuplicate = async (s: Segment) => {
    try {
      const copy = await create.mutateAsync({
        body: {
          name: `${s.name} (copy)`,
          ...(s.description ? { description: s.description } : {}),
          source: s.source || 'users',
          source_params: s.source_params || {},
          filters: s.filters || {},
        },
        idempotencyKey: generateIdempotencyKey(),
      });
      toast.success('Segment duplicated', { richColors: true });
      navigate(`/admin/relationship/segments/${segmentId(copy)}`);
    } catch (error: any) {
      toast.error(error?.message || 'Duplicate failed', { richColors: true });
    }
  };

  if (permLoading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="mx-auto max-w-screen-2xl space-y-4">
          <Skeleton className="h-10 w-48" />
          <Skeleton className="h-40 w-full" />
        </div>
      </div>
    );
  }

  if (!canView) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="mx-auto max-w-screen-2xl">
          <AccessDenied />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto max-w-screen-2xl space-y-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="mb-1 flex items-center gap-2.5">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-600">
                <UsersRound className="h-4 w-4 text-white" />
              </div>
              <h1 className="text-2xl font-black text-gray-900">Segments</h1>
            </div>
            <p className="text-sm text-gray-500">
              Saved audiences, resolved at send time: platform users, the waitlist, sales prospects who agreed to email, or an uploaded list.
            </p>
          </div>
          {canWrite && (
            <Button asChild className="gap-2">
              <Link to="/admin/relationship/segments/new">
                <Plus className="h-4 w-4" />
                New segment
              </Link>
            </Button>
          )}
        </div>

        <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
          {isLoading && (
            <div className="space-y-3 p-4">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          )}
          {isError && (
            <p className="p-8 text-center text-sm text-red-600">Failed to load segments.</p>
          )}
          {!isLoading && !isError && segments.length === 0 && (
            <div className="p-12 text-center">
              <UsersRound className="mx-auto mb-3 h-12 w-12 text-gray-300" />
              <p className="text-sm text-gray-500">No segments yet.</p>
            </div>
          )}
          {!isLoading && segments.length > 0 && (
            <div className="overflow-x-auto">
              <table className="min-w-full text-left text-sm">
                <thead className="border-b bg-gray-50 text-xs uppercase text-gray-500">
                  <tr>
                    <th className="px-4 py-3 font-medium">Name</th>
                    <th className="px-4 py-3 font-medium">Source</th>
                    <th className="px-4 py-3 font-medium">Est. size</th>
                    <th className="px-4 py-3 font-medium">Updated</th>
                    <th className="w-12 px-4 py-3 font-medium" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {segments.map((s) => {
                    const sid = segmentId(s);
                    return (
                      <tr
                        key={sid}
                        className="cursor-pointer hover:bg-gray-50"
                        onClick={() => navigate(`/admin/relationship/segments/${sid}`)}
                      >
                        <td className="px-4 py-3">
                          <Link
                            to={`/admin/relationship/segments/${sid}`}
                            className="font-medium text-blue-700 hover:underline"
                          >
                            {s.name}
                          </Link>
                          {s.description && (
                            <p className="text-xs text-gray-500">{s.description}</p>
                          )}
                        </td>
                        <td className="px-4 py-3 text-gray-600">
                          {SOURCE_LABEL[s.source || 'users'] || s.source}
                        </td>
                        <td className="px-4 py-3">
                          {s.estimated_size != null
                            ? s.estimated_size.toLocaleString()
                            : '—'}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-gray-500">
                          {formatEpoch(s.updated_at || s.created_at)}
                        </td>
                        <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" aria-label={`Actions for ${s.name}`}>
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-48">
                              <DropdownMenuItem
                                className="gap-2"
                                onClick={() => navigate(`/admin/relationship/segments/${sid}`)}
                              >
                                <Eye className="h-4 w-4" />
                                {canWrite ? 'Review and edit' : 'Review'}
                              </DropdownMenuItem>
                              {canWrite && (
                                <>
                                  <DropdownMenuItem className="gap-2" onClick={() => handleDuplicate(s)}>
                                    <Copy className="h-4 w-4" />
                                    Duplicate
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem
                                    className="gap-2 text-red-600 focus:text-red-700"
                                    onClick={() => setToDelete(s)}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                    Delete
                                  </DropdownMenuItem>
                                </>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete &ldquo;{toDelete?.name}&rdquo;?</AlertDialogTitle>
            <AlertDialogDescription>
              Campaigns that target this segment will lose these people from their audience. This can&rsquo;t be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={del.isPending}
              onClick={(e) => {
                e.preventDefault();
                handleDelete();
              }}
              className="bg-red-600 hover:bg-red-700"
            >
              {del.isPending ? 'Deleting…' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

const SOURCE_LABEL: Record<string, string> = {
  users: 'Platform users',
  waitlist: 'Waitlist',
  prospects: 'Sales prospects',
  upload: 'Uploaded list',
};

export default SegmentListPage;
